import { useMemo } from 'react';

import { useCrowdfundingProvider } from '#Context/crowdfunding/Crowdfunding.provider.js';
import { CampaignPrize, PeerTypes } from '@givebacks/api/store';
import { useCreateSearchFn } from 'core/Components';

import { useListCampaignPrize } from '../../../../../../../api/new/campaigns/prizes/get';

const useSearchPeerPrizes = (
  searches,
  {
    causeId,
    campaignId,
    peerType,
  }: { causeId: string; campaignId: string; peerType: 'team' | 'participant' }
) => {
  return useListCampaignPrize({
    causeId,
    campaignId,
    queryOptions: {
      searches: {
        ...searches,
        peer_type: {
          value: peerType,
        },
      },
    },
  });
};

const useSearchPeerPrizesFn = (
  causeId: string,
  campaignId: string,
  peerType: 'team' | 'participant'
) =>
  useCreateSearchFn({
    useSearchFn: useSearchPeerPrizes,
    select: (data) =>
      data?.map(({ title, ...rest }) => ({
        label: title,
        value: rest.uuid,
      })),
    searchFields: ['prize_id'],
    params: { causeId, campaignId, peerType },
  });

const usePeerPrizeReport = (prizeReport?: CampaignPrize[]) => {
  return useMemo(() => {
    if (!prizeReport) {
      return {
        hasEarnedPrizes: false,
        hasFulfilledPrizes: false,
        earnedPrizes: [],
        fulfilledPrizes: [],
        allEarnedPrizesFulfilled: false,
      };
    }

    // Check if any prizes have been earned
    const hasEarnedPrizes = prizeReport.some((prize) => prize.earned);
    // Check if any prizes have been fulfilled
    const hasFulfilledPrizes = prizeReport.some((prize) => prize.fulfilled);

    // list of earned prizes to set initial value of fulfill modal
    const earnedPrizes = prizeReport.filter((prize) => prize.earned);
    // list of fulfilled prizes to set initial value of unfulfill modal
    const fulfilledPrizes = prizeReport.filter((prize) => prize.fulfilled);

    // check if all earned prizes are fulfilled
    const allEarnedPrizesFulfilled = earnedPrizes.every((prize) => prize.fulfilled);

    return {
      hasEarnedPrizes,
      hasFulfilledPrizes,
      earnedPrizes,
      fulfilledPrizes,
      allEarnedPrizesFulfilled,
    };
  }, [prizeReport]);
};

// Check if there are any prizes for a given peer type
const useHasCampaignPrizes = (peerType: PeerTypes) => {
  const { causeId, campaignId } = useCrowdfundingProvider();

  const { data: prizes } = useListCampaignPrize({
    causeId,
    campaignId,
    queryOptions: {
      searches: {
        peer_type: {
          value: peerType === 'teams' ? 'team' : 'participant',
        },
      },
    },
  });

  return useMemo(() => {
    return prizes && prizes.length > 0;
  }, [prizes]);
};

export { useHasCampaignPrizes, usePeerPrizeReport, useSearchPeerPrizesFn };
